import { Controller } from "stimulus"

// Video index filtering + search.
// Reads `?category=<slug>` from the URL on connect, then filters the card grid
// by the active category pill and the search box (title + blurb + category).
// Filter counts and the empty state track the current search. Stimulus
// (re)connects automatically on Turbo navigation, so no turbo:load wiring.
export default class VideosController extends Controller {

  static get targets() {
    return ['filter', 'card', 'search', 'clear', 'empty', 'emptyQuery']
  }

  connect() {
    const params = new URLSearchParams(window.location.search)
    this.activeCategory = this.resolve(params.get('category'))
    this.query = ''
    this.render()
  }

  // Click handler for the category pills.
  filter(event) {
    this.activeCategory = event.currentTarget.getAttribute('data-video-category')
    this.render()
    this.updateUrl(this.activeCategory)
  }

  // Input handler for the search box.
  search(event) {
    this.query = event.currentTarget.value
    this.render()
  }

  clear() {
    this.query = ''
    if (this.hasSearchTarget) {
      this.searchTarget.value = ''
      this.searchTarget.focus()
    }
    this.render()
  }

  render() {
    const q = this.query.trim().toLowerCase()
    const matchesSearch = (card) =>
      !q || (card.getAttribute('data-search') || '').indexOf(q) !== -1

    // Filter counts reflect the current search results; also sync active pill.
    this.filterTargets.forEach((btn) => {
      const cat = btn.getAttribute('data-video-category')
      const n = this.cardTargets.filter((card) =>
        matchesSearch(card) && (cat === 'all' || card.getAttribute('data-category') === cat)
      ).length
      const count = btn.querySelector('.video-filter-count')
      if (count) count.textContent = n
      btn.classList.toggle('is-active', cat === this.activeCategory)
    })

    // Show/hide each card by search + active category.
    let visible = 0
    this.cardTargets.forEach((card) => {
      const inCategory = this.activeCategory === 'all' ||
        card.getAttribute('data-category') === this.activeCategory
      const show = matchesSearch(card) && inCategory
      card.style.display = show ? '' : 'none'
      if (show) visible += 1
    })

    if (this.hasClearTarget) this.clearTarget.style.display = q ? '' : 'none'
    if (this.hasEmptyTarget) this.emptyTarget.style.display = visible === 0 ? '' : 'none'
    if (this.hasEmptyQueryTarget) this.emptyQueryTarget.textContent = this.query.trim()
  }

  // Map an arbitrary ?category value to a real filter, falling back to 'all'.
  resolve(value) {
    const slug = this.slugify(value)
    if (!slug) return 'all'
    const known = this.filterTargets.some((btn) => btn.getAttribute('data-video-category') === slug)
    return known ? slug : 'all'
  }

  updateUrl(category) {
    const url = new URL(window.location.href)
    if (category === 'all') {
      url.searchParams.delete('category')
    } else {
      url.searchParams.set('category', category)
    }
    // Preserve Turbo's restoration identifier in history.state (see CLAUDE.md).
    history.replaceState(history.state, '', url.toString())
  }

  slugify(value) {
    if (!value) return null
    return value.toString().trim().toLowerCase().replace(/\s+/g, '-')
  }

}
